import { Geom, Pair, Ring, union } from 'polygon-clipping';
import { getOverlappingPoints, roundPoint } from '../utils';
import { POINT_MATCH_THRESHOLD } from '../constant';

const AREA_EQUALITY_TOLERANCE = 0.5;
// vertices closer than this are the same point of a shared edge (drawing/float noise);
// same threshold the tool uses elsewhere to treat two facet points as touching
const SHARED_POINT_SNAP_THRESHOLD = POINT_MATCH_THRESHOLD;
// merged area may differ from the sum of both facets by at most this fraction
const MERGED_AREA_TOLERANCE = 0.02;
// max triangle area for a vertex to count as lying on the line of its neighbours
const COLLINEAR_AREA_TOLERANCE = 0.75;
// two facets are considered adjacent (shared) only if they touch along an edge,
// i.e. they have at least two overlapping vertices
const MIN_SHARED_POINTS = 2;

function toRing(coordinates: number[][]): Ring {
	const ring: Ring = [];

	for (const [x, y] of coordinates) {
		const last = ring[ring.length - 1];
		if (!last || last[0] !== x || last[1] !== y) {
			ring.push([x, y] as Pair);
		}
	}

	if (ring.length > 2) {
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			ring.push([first[0], first[1]] as Pair);
		}
	}

	return ring;
}

// Shoelace area of a facet's coordinates
function getFacetArea(coordinates: number[][]): number {
	const ring = toRing(coordinates);
	if (ring.length < 4) return 0;

	let area = 0;
	for (let i = 0; i < ring.length - 1; i++) {
		area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
	}

	return Math.abs(area / 2);
}

/** ids of facets sharing an edge with the given facet */
function getAdjacentFacetIds(segmentId: string, facets: editableFacet[]): string[] {
	const facet = facets.find(eachFacet => eachFacet.segmentId === segmentId);
	if (!facet?.coordinates?.length) return [];

	const sharedCount: Record<string, number> = {};
	const overlappingPoints = getOverlappingPoints(facet, facets);

	overlappingPoints.forEach(({ overlappedWith }: { overlappedWith: Record<string, unknown> }) => {
		Object.keys(overlappedWith ?? {}).forEach(id => {
			sharedCount[id] = (sharedCount[id] || 0) + 1;
		});
	});

	return Object.keys(sharedCount).filter(id => sharedCount[id] >= MIN_SHARED_POINTS);
}

/**
 * ids of the facets that can still be picked for the current merge selection:
 * every facet adjacent to at least one of the already selected facets.
 */
function getMergeableFacetIds(selectedIds: string[], facets: editableFacet[], deletedIds: string[] = []): string[] {
	if (!selectedIds.length) {
		return facets
			.filter(facet => facet.coordinates.length && !deletedIds.includes(facet.segmentId))
			.map(facet => facet.segmentId);
	}

	const mergeable = new Set<string>();
	selectedIds.forEach(id => {
		getAdjacentFacetIds(id, facets).forEach(adjacentId => {
			if (!selectedIds.includes(adjacentId) && !deletedIds.includes(adjacentId)) {
				mergeable.add(adjacentId);
			}
		});
	});

	return Array.from(mergeable);
}

/** closest point of segment v->w to p, plus its distance */
function projectOnSegment(p: Pair, v: Pair, w: Pair): { point: Pair, distance: number } {
	const lengthSquared = (w[0] - v[0]) ** 2 + (w[1] - v[1]) ** 2;
	const t = lengthSquared === 0
		? 0
		: Math.max(0, Math.min(1, ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / lengthSquared));
	const point: Pair = [v[0] + t * (w[0] - v[0]), v[1] + t * (w[1] - v[1])];

	return { point, distance: Math.hypot(p[0] - point[0], p[1] - point[1]) };
}

/**
 * Pulls the ring onto the reference ring wherever they touch: first onto a matching
 * vertex, otherwise onto the touched edge. Without this the shared edge of the two
 * facets is only *almost* coincident and the union keeps them as two polygons.
 */
function snapRingToReference(ring: Ring, reference: Ring): Ring {
	return ring.map((point) => {
		let snapped: Pair = [point[0], point[1]];
		let closestDistance = SHARED_POINT_SNAP_THRESHOLD;

		for (const [rx, ry] of reference) {
			const distance = Math.hypot(point[0] - rx, point[1] - ry);
			if (distance < closestDistance) {
				closestDistance = distance;
				snapped = [rx, ry];
			}
		}

		if (closestDistance < SHARED_POINT_SNAP_THRESHOLD) return snapped;

		// no matching vertex: snap onto the touched edge so both edges become collinear
		for (let i = 0; i < reference.length - 1; i++) {
			const projected = projectOnSegment(point, reference[i], reference[i + 1]);
			if (projected.distance < closestDistance) {
				closestDistance = projected.distance;
				snapped = projected.point;
			}
		}

		return snapped;
	});
}

/**
 * Drops the leftover vertices of the seam between the merged facets: after the union
 * those points sit on the straight boundary of the merged facet, so removing them
 * leaves a clean closed polygon. Points still touching a facet outside the merge are
 * kept, otherwise the merged facet would lose its adjacency for the next merge.
 */
function removeSeamPoints(coordinates: number[][], seamPoints: number[][], keepPoints: number[][]): number[][] {
	const isNear = (point: number[], others: number[][], threshold = SHARED_POINT_SNAP_THRESHOLD) =>
		others.some(([ox, oy]) => Math.hypot(point[0] - ox, point[1] - oy) < threshold);

	// exact duplicates left behind by the union
	let points = coordinates.filter((point, index, all) => {
		const previous = all[(index - 1 + all.length) % all.length];
		return !(Math.abs(point[0] - previous[0]) < 1e-6 && Math.abs(point[1] - previous[1]) < 1e-6);
	});

	let removed = true;
	while (removed && points.length > 3) {
		removed = false;

		for (let i = 0; i < points.length; i++) {
			const previous = points[(i - 1 + points.length) % points.length];
			const current = points[i];
			const next = points[(i + 1) % points.length];

			if (!isNear(current, seamPoints) || isNear(current, keepPoints)) continue;

			const triangleArea = Math.abs(
				(current[0] - previous[0]) * (next[1] - previous[1]) -
				(next[0] - previous[0]) * (current[1] - previous[1])
			) / 2;

			if (triangleArea <= COLLINEAR_AREA_TOLERANCE) {
				points = points.filter((_, index) => index !== i);
				removed = true;
				break;
			}
		}
	}

	return points;
}

function pickLargestPolygon(multiPolygon: Ring[][]): Ring[] | null {
	let largest: Ring[] | null = null;
	let largestArea = -1;

	for (const polygon of multiPolygon) {
		if (!polygon?.[0]?.length) continue;
		const area = getFacetArea(polygon[0] as unknown as number[][]);
		if (area > largestArea) {
			largestArea = area;
			largest = polygon;
		}
	}

	return largest;
}

function ringToCoordinates(ring: Ring): number[][] {
	const coordinates: number[][] = [];

	for (let i = 0; i < ring.length; i++) {
		// drop the closing point
		if (i === ring.length - 1 && ring[i][0] === ring[0][0] && ring[i][1] === ring[0][1]) break;
		coordinates.push(roundPoint([ring[i][0], ring[i][1]]));
	}

	return coordinates;
}

export type FacetMergeResult = {
	/** facet that survives the merge, holding the merged geometry */
	mergedFacet: editableFacet;
	/** facets merged into the surviving one, to be deleted */
	removedFacetIds: string[];
}

/**
 * Merges the selected facets one by one: the smaller facet is always merged into
 * the larger one. When two facets have the same area, the one clicked first wins.
 */
function mergeSelectedFacets(selectedIds: string[], facets: editableFacet[]): FacetMergeResult | null {
	const selectedFacets = selectedIds
		.map(id => facets.find(facet => facet.segmentId === id))
		.filter((facet): facet is editableFacet => !!facet?.coordinates?.length);

	if (selectedFacets.length < 2) return null;

	// largest first; equal areas keep the click order (Array.sort is stable)
	const ordered = [...selectedFacets].sort((facetA, facetB) => {
		const areaDiff = getFacetArea(facetB.coordinates) - getFacetArea(facetA.coordinates);
		return Math.abs(areaDiff) <= AREA_EQUALITY_TOLERANCE ? 0 : areaDiff;
	});

	const targetFacet = ordered[0];
	let accumulated: Ring[] = [toRing(targetFacet.coordinates)];
	const seamPoints: number[][] = [];

	// vertices of the facets left untouched by this merge: the merged facet must keep
	// them so it stays adjacent to its neighbours for the next merge
	const keepPoints = facets
		.filter(facet => !selectedIds.includes(facet.segmentId) && facet.coordinates.length)
		.flatMap(facet => facet.coordinates);

	// merge one facet at a time into the accumulated polygon (largest first)
	for (const facet of ordered.slice(1)) {
		const ring = toRing(facet.coordinates);
		if (ring.length < 4) continue;

		// make the shared edge exactly coincident, otherwise the union keeps slivers
		const snappedRing = toRing(snapRingToReference(ring, accumulated[0]) as unknown as number[][]);
		const referenceRing = accumulated[0];
		snappedRing.forEach(([x, y]) => {
			const touchesReference = referenceRing.some(([ax, ay]) => Math.hypot(ax - x, ay - y) < SHARED_POINT_SNAP_THRESHOLD);
			if (touchesReference) seamPoints.push([x, y]);
		});

		const unionResult = union(accumulated as Geom, [snappedRing] as Geom);
		const largest = pickLargestPolygon(unionResult as Ring[][]);
		if (!largest) return null;

		accumulated = largest;
	}

	const mergedCoordinates = removeSeamPoints(ringToCoordinates(accumulated[0]), seamPoints, keepPoints);
	if (mergedCoordinates.length < 3) return null;

	// the union must cover both facets, otherwise the shared edge was not coincident
	// and one of the facets would silently be dropped
	const expectedArea = selectedFacets.reduce((total, facet) => total + getFacetArea(facet.coordinates), 0);
	const mergedArea = getFacetArea(mergedCoordinates);
	if (Math.abs(mergedArea - expectedArea) > expectedArea * MERGED_AREA_TOLERANCE) {
		console.warn('[merge facets] merged area mismatch', { mergedArea, expectedArea });
		return null;
	}

	return {
		mergedFacet: {
			...targetFacet,
			coordinates: mergedCoordinates,
			isEdited: true,
			isSelected: false,
		},
		removedFacetIds: ordered.slice(1).map(facet => facet.segmentId),
	};
}

export {
	getFacetArea,
	getAdjacentFacetIds,
	getMergeableFacetIds,
	mergeSelectedFacets,
};
